import { useEffect, useRef, useState } from "react";
import { DataTable, DataTableSortEvent } from "primereact/datatable";
import { Column } from "primereact/column";
import { useDispatch, useSelector } from "react-redux";
import "./PreventiveAppointmentGrid.scss";
import PreventiveAppointmentDetailsPopup from "../../PreventiveAppointmentDetailsPopup";
import UpdateItemStatusPopup from "src/components/UpdateItemStatus/UpdateItemStatusPopup";
import {
  setIsUpdateStatusPopupOpen,
  setSelectedId,
  setPopupType,
  setPreSelectedStatus,
  setOnHoldReason,
} from "src/redux/slices/sharedSlice";
import {
  setIsPreventiveAppointmentDetailsPopupOpen,
  setPreventiveAppointmentPopupData,
  selectPreventiveAppointments,
  setNumberOfRowsPerPage,
  setGridCurrentPage,
  getPreventiveAppointment,
  setFilterDataField,
  retrieveAppointmentStatusForEdit,
} from "src/redux/slices/preventiveAppointmentSlice";
import { PopupType } from "src/resources/enum";
import { useTranslation } from "react-i18next";
import {
  convertDateToFullDateString,
  getTextValueById,
  isArabic,
  isRtl,
  isCivilDefenseUser,
  formatDateV2,
} from "src/utils/helpers";
import GridPaginator from "src/components/GridPaginator/GridPaginator";
import StatusColumnTemplate from "src/components/UpdateItemStatus/StatusColumnTemplate";
import { SortOrderDefault } from "src/resources/constant";
import moment from "moment";

const PreventiveMaintenanceGrid = () => {
  const dispatch = useDispatch();
  const { totalRowsCount, numberOfRowsPerPage, gridCurrentPage } = useSelector(
    (state: any) => state.preventiveAppointments
  );
  const pathName = window.location.pathname;
  const [isBreached, setIsBreached] = useState<any>(
    pathName?.includes("breached") ? true : false
  );
  const { t } = useTranslation(["common", "assignBuilding", "registration"]);
  const { isUpdateStatusPopupOpen } = useSelector((state: any) => state.shared);
  const [sortOrder, setSortOrder] = useState<any | null>(SortOrderDefault);
  const preventiveAppointmentData = useSelector(selectPreventiveAppointments);
  const { appointmentsStatuses, allServiceTerritories, language } = useSelector(
    (state: any) => state.lookup
  );
  const { sortColumn } = useSelector(
    (state: any) => state.preventiveAppointments.filterData
  );
  const appointmentStatusForEdit = useSelector(
    (state: any) => state.preventiveAppointments.appointmentStatusForEdit
  );

  const setUpdateStatusPopupOpen = (rowData: any) => {
    dispatch(setIsUpdateStatusPopupOpen(true));
    dispatch(setSelectedId(rowData.appointmentId));
    dispatch(setPopupType(PopupType.Appointment));
    dispatch(setPreSelectedStatus(rowData.statusId));
    dispatch(setOnHoldReason(rowData.onHoldReasonId));
  };

  const tableRef =
    useRef<DataTable<(typeof preventiveAppointmentData)[0]>>(null);

  const appointmentDateTemplate = (rowData: any) => {
    const date = convertDateToFullDateString(rowData?.appointmentDate);
    return (
      <div
        onClick={() => handleAppointmentNumberClick(rowData)}
        className={`d-flex flex-row align-items-center date-gap`}
      >
        <h1
          className={`date-day m-0 ${
            Number(date.day) < 10 ? "single-day" : ""
          }`}
        >
          {date.day}
        </h1>
        <div className="date-month-year">
          <span className="d-flex flex-column">{date.month}</span>
          <span>{date.year}</span>
        </div>
      </div>
    );
  };

  const getAppointmentType = (rowData: any) => {
    if (isRtl(language)) return rowData.appointmentTypeAr;
    else return rowData.appointmentTypeEn;
  };
  const successCallback = () => {
    dispatch(getPreventiveAppointment() as any);
  };

  const StatusTemplate = (rowData: any) => {
    return (
      <StatusColumnTemplate
        statuses={appointmentsStatuses}
        rowData={rowData}
        setUpdateStatusPopupOpen={setUpdateStatusPopupOpen}
      />
    );
  };

  const handleAppointmentNumberClick = (rowData: any) => {
    if (!isUpdateStatusPopupOpen) {
      dispatch(
        setPreventiveAppointmentPopupData({
          field: "appointmentId",
          value: rowData.appointmentId,
        })
      );
      dispatch(
        setPreventiveAppointmentPopupData({
          field: "status",
          value: rowData.statusId,
        })
      );
      dispatch(
        setPreventiveAppointmentPopupData({
          field: "statusReason",
          value: rowData.statusReason,
        })
      );
      dispatch(setIsPreventiveAppointmentDetailsPopupOpen(true));
    }
  };
  const handleRowClick = (row: any) => {
    handleAppointmentNumberClick(row);
    // You can do whatever you need with the row data here
  };

  const onSort = (e: DataTableSortEvent) => {
    let sortFieldBEName = null;
    setSortOrder(e.sortOrder);
    if (e.sortField === "propertyNumber") sortFieldBEName = "propertyID";
    else if (e.sortField === "territory")
      sortFieldBEName = "serviceTerritoryId";
    dispatch(
      setFilterDataField({
        field: "sortColumn",
        value: sortFieldBEName ?? e.sortField,
      })
    );
    dispatch(
      setFilterDataField({ field: "isAscending", value: e.sortOrder === 1 })
    );
    dispatch(setGridCurrentPage(0));
  };

  const serviceTerritoryTemplate = (rowData: any) => {
    return (
      <span>
        {getTextValueById(
          rowData?.serviceTerritoryId,
          allServiceTerritories as unknown as [],
          "territoryId"
        )}
      </span>
    );
  };

  const rowClassName = (rowData: any) => {
    let color = "black";
    const formattedToday = formatDateV2(new Date().toISOString());
    const currentDate = moment(formattedToday, "DD/MMM/YYYY")
      .startOf("day")
      .toDate();
    const appointmentDate = moment(rowData.appointmentDate, "DD/MMM/YYYY")
      .startOf("day")
      .toDate();
    const appointmentTimestamp = appointmentDate.getTime();
    const currentTimestamp = currentDate.getTime();
    if (appointmentTimestamp < currentTimestamp) {
      color = rowData.sLABreached ? "red" : "gray";
    } else if (appointmentTimestamp === currentTimestamp) {
      color = "green";
    } else if (appointmentTimestamp > currentTimestamp) {
      color = "blue";
    }
    return color;
  };

  useEffect(() => {
    if (!appointmentStatusForEdit.length)
      dispatch(retrieveAppointmentStatusForEdit() as any);
  }, [dispatch]);

  return (
    <div className="grid-outer-wrapper">
      <div className="grid-inner-wrapper">
        <DataTable
          onSort={onSort}
          ref={tableRef}
          lazy
          className="maintenance-requests-grid preventive-grid px-3 p-datatable-clickable-header"
          value={preventiveAppointmentData}
          onRowClick={(e) => handleRowClick(e.data)}
          key="appointmentId"
          emptyMessage={t("emptyRecords")}
          sortField={sortColumn || undefined}
          sortOrder={sortOrder || null} // Ensure sortOrder is a number or null
          rowClassName={rowClassName}
          scrollable
        >
          <Column
            sortable
            field="appointmentDate"
            header={t("appointmentDate")}
            body={appointmentDateTemplate}
            className="plain-text-date lg-col"
            columnKey="appointmentId"
            frozen
            alignFrozen={isArabic() ? "right" : "left"}
          />
          <Column
            field="appointmentTypeEn"
            header={t("appointmentType")}
            body={getAppointmentType}
            sortable
            className="md-col"
          />
          {isCivilDefenseUser() ? (
            <Column
              field="companyName"
              header={t("bmcCompanyName")}
              // sortable
              className="lg-col"
            ></Column>
          ) : null}
          <Column
            sortable
            field="status"
            header={t("status")}
            body={StatusTemplate}
            className="lg-col"
          ></Column>
          <Column
            field="propertyNumber"
            header={t("propertyID")}
            className="lg-col"
          />
           <Column
            field="PRPNumber"
            header={t("PRPNumber")}
            className="lg-col"
          />
           <Column
            field="PropertyTradeLicense"
            header={t("propertyTradeLicense")}
            className="lg-col"
          />
 
          <Column
            field="buildingName"
            sortable
            header={t("buildingNameHeader")}
            className="lg-col"
          />
          <Column field="latitude" header={t("latitude")} className="sm-col" />
          <Column
            field="longitude"
            header={t("longitude")}
            className="sm-col"
          />
          <Column
            field="territory"
            body={serviceTerritoryTemplate}
            header={t("territory")}
            // sortable
            className="md-col"
          />

          {!isBreached && (
            <Column
              field="completionDate"
              header={t("completionDate")}
              className="plain-text-date md-col"
              sortable
            />
          )}
        </DataTable>
      </div>
      <GridPaginator
        totalRowsCount={totalRowsCount}
        currentPage={gridCurrentPage}
        numberOfRowsPerPage={numberOfRowsPerPage}
        setNumberOfRowsPerPage={setNumberOfRowsPerPage}
        setGridCurrentPage={setGridCurrentPage}
        tableRef={tableRef}
      />
      <PreventiveAppointmentDetailsPopup />
      {isUpdateStatusPopupOpen && (
        <UpdateItemStatusPopup successCallbackFn={successCallback} />
      )}
    </div>
  );
};

export default PreventiveMaintenanceGrid;
